import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, RegisterCredentials, AuthContextType } from '../types/auth';
import { authApi, ApiError } from '../services/api';
import { 
  getCachedUser, 
  cacheUser, 
  clearAuthData, 
  hasValidTokens, 
  isCacheValid,
  preloadAuthState 
} from '../utils/authOptimization';
import { preloadService } from '../services/preloadService';
import { cacheService } from '../services/cacheService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Preload auth state synchronously for instant loading
  const preloadedState = preloadAuthState();
  const [user, setUser] = useState<User | null>(preloadedState.user);
  const [loading, setLoading] = useState(!preloadedState.user && preloadedState.hasToken);

  useEffect(() => {
    const initializeAuth = async () => {
      const hasToken = hasValidTokens();
      const cachedUser = getCachedUser();
      
      if (hasToken) {
        if (cachedUser && isCacheValid()) {
          // We already have valid cached data, just validate in background
          setUser(cachedUser);
          setLoading(false);
          
          // Background validation - don't block UI
          setTimeout(async () => {
            try {
              const freshUserData = await authApi.getProfile();
              setUser(freshUserData as User);
              cacheUser(freshUserData as User);
              
              // Preload user data for existing authenticated users
              preloadService.preloadUserData().catch((error) => {
                console.warn('Background preloading failed:', error);
              });
            } catch (error) {
              // Token is invalid, clear everything
              clearAuthData();
              setUser(null);
            }
          }, 50); // Very small delay to not block render
          
          return;
        }
        
        // No valid cache, make API call
        try {
          const userData = await authApi.getProfile();
          setUser(userData as User);
          cacheUser(userData as User);
          
          // Preload user data for newly authenticated users
          preloadService.preloadUserData().catch((error) => {
            console.warn('Background preloading failed:', error);
          });
        } catch (error) {
          // Token is invalid, clear everything
          clearAuthData();
          setUser(null);
        }
      } else {
        // No token, ensure everything is cleared
        clearAuthData();
        setUser(null);
      }
      
      setLoading(false);
    };

    // Only initialize if we don't already have cached data
    if (!preloadedState.user || !isCacheValid()) {
      initializeAuth();
    } else {
      setLoading(false);
    }
  }, [preloadedState.user]);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authApi.login(credentials);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Cache user data immediately
      cacheUser(response.user);
      setUser(response.user);

      // Preload user's chat history and companion data in the background
      // This runs asynchronously and doesn't block the login flow
      preloadService.preloadUserData().catch((error) => {
        console.warn('Background preloading failed:', error);
      });
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.message);
      }
      throw new Error('Login failed');
    }
  };

  const register = async (credentials: RegisterCredentials) => {
    try {
      await authApi.register(credentials);
    } catch (error) {
      if (error instanceof ApiError) {
        throw new Error(error.message);
      }
      throw new Error('Registration failed');
    }
  };

  const logout = () => {
    clearAuthData();
    setUser(null);
    
    // Clear chat cache on logout for privacy
    cacheService.clearChatCache();
    preloadService.clear();
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    cacheUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    loading,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};